import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from "react";
import type { ArchiveHistoryFolder, ArchiveHistoryV2, EmailData, FolderItem } from "../../../../common/src/types/types";
import { getAccessTokenForDialog, getArchiveHistory, getUserFavorites, saveEmailsToFolder } from "../../apiClient";
import { getSetting, getSelectedEmailsInfo, setFolderMap, setKeywordsMap } from "../taskpane";
import type { ToastVariant } from "../components/Toast";

export type AppView = "folders" | "project" | "addFavorites" | "save" | "history" | "settings";

interface AppContextType {
    // Navigation
    currentView: AppView;
    openedProject: string | null;
    navigateToFolders: () => void;
    navigateToProject: (projectId: string) => void;
    navigateToAddFavorites: () => void;
    navigateToSave: () => void;
    navigateToHistory: () => void;
    navigateToSettings: () => void;

    // Data
    favorites: FolderItem[];
    setFavorites: React.Dispatch<React.SetStateAction<FolderItem[]>>;
    bodyStoragePrefs: Record<string, boolean>;
    handleBodyStoragePreference: (folderId: string, enabled: boolean) => void;
    searchTerm: string;
    setSearchTerm: (term: string) => void;
    selectedFolder: string | null;
    setSelectedFolder: (folderId: string | null) => void;
    selectedFolders: string[];
    setSelectedFolders: React.Dispatch<React.SetStateAction<string[]>>;

    // Archive status refresh trigger
    archiveStatusVersion: number;

    // Actions
    handleSaveEmails: (projectId: string, emails: any[]) => void;
    handleAddToFavorites: (projectId: string) => void;
    refreshFavorites: () => Promise<FolderItem[]>;
    openEmailPreviewDialog: (email: EmailData, setSelectedEmail?: React.Dispatch<React.SetStateAction<EmailData | null>>, handleRemoveEmailBody?: (emailId: string) => Promise<void>) => void;
    quickSaveEmails: (folderId: string) => Promise<boolean>;
}

const AppContext = createContext<AppContextType | null>(null);

export function useAppContext(): AppContextType {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error("useAppContext must be used within an AppProvider");
    }
    return context;
}

interface AppState {
    currentView: AppView;
    openedProject: string | null;
    bodyStoragePrefs?: Record<string, boolean>;
}

const APP_STATE_KEY = "efile-app-state";

function loadAppState(): AppState {
    try {
        const saved = localStorage.getItem(APP_STATE_KEY);
        if (saved) {
            const state = JSON.parse(saved);
            // Handle legacy format with boolean flags
            if ("showAddToFavorites" in state) {
                let view: AppView = "folders";
                if (state.showSettingsView) view = "settings";
                else if (state.showArchiveHistory) view = "history";
                else if (state.showSaveView) view = "save";
                else if (state.showAddToFavorites) view = "addFavorites";
                else if (state.openedProject) view = "project";
                return {
                    currentView: view,
                    openedProject: state.openedProject ?? null,
                    bodyStoragePrefs: state.bodyStoragePrefs ?? {},
                };
            }
            return {
                currentView: state.currentView ?? "folders",
                openedProject: state.openedProject ?? null,
                bodyStoragePrefs: state.bodyStoragePrefs ?? {},
            };
        }
    } catch (error) {
        console.warn("Failed to load app state:", error);
    }
    return {
        currentView: "folders",
        openedProject: null,
        bodyStoragePrefs: {},
    };
}

function saveAppState(state: AppState): void {
    try {
        localStorage.setItem(APP_STATE_KEY, JSON.stringify(state));
    } catch (error) {
        console.warn("Failed to save app state:", error);
    }
}

export function AppProvider({ children }: { children: ReactNode }) {
    const initialState = loadAppState();

    const [currentView, setCurrentView] = useState<AppView>(initialState.currentView);
    const [openedProject, setOpenedProject] = useState<string | null>(initialState.openedProject);
    const [favorites, setFavorites] = useState<FolderItem[]>([]);
    const [bodyStoragePrefs, setBodyStoragePrefs] = useState<Record<string, boolean>>(
        initialState.bodyStoragePrefs ?? {}
    );
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
    const [selectedFolders, setSelectedFolders] = useState<string[]>([]);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState("Emails queued for archiving");
    const [toastVariant, setToastVariant] = useState<ToastVariant>("success");
    const toastQueueRef = useRef<{ message: string; variant: ToastVariant }[]>([]);
    const previousStatusesRef = useRef<Map<string, string>>(new Map());
    const showToastRef = useRef(false);
    const [archiveStatusVersion, setArchiveStatusVersion] = useState(0);
    const dialogRef = useRef<Office.Dialog | null>(null);

    // Navigation
    const navigateToFolders = useCallback(() => {
        setCurrentView("folders");
        setOpenedProject(null);
        setSelectedFolder(null);
        setSelectedFolders([]);
        setSearchTerm("");
    }, []);

    const navigateToProject = useCallback((projectId: string) => {
        setCurrentView("project");
        setOpenedProject(projectId);
    }, []);

    const navigateToAddFavorites = useCallback(() => {
        setCurrentView("addFavorites");
        setOpenedProject(null);
    }, []);

    const navigateToSave = useCallback(() => {
        setCurrentView("save");
    }, []);

    const navigateToHistory = useCallback(() => {
        setCurrentView("history");
        setOpenedProject(null);
    }, []);

    const navigateToSettings = useCallback(() => {
        setCurrentView("settings");
        setOpenedProject(null);
    }, []);

    // Data actions
    const handleBodyStoragePreference = useCallback((folderId: string, enabled: boolean) => {
        setBodyStoragePrefs((prev) => ({ ...prev, [folderId]: enabled }));
    }, []);

    const handleSaveEmails = (projectId: string, emails: any[]) => {
        if (openedProject) {
            setCurrentView("project");
        } else {
            setCurrentView("folders");
        }
    };

    const handleAddToFavorites = useCallback((projectId: string) => {
    }, []);

    const quickSaveEmails = useCallback(async (folderId: string): Promise<boolean> => {
        if (!getSetting("skipArchiveConfirmation", false)) return false;
        try {
            const emails = await getSelectedEmailsInfo();
            const deleteAfterArchive = getSetting("deleteArchivedEmails", false);
            if (emails.length === 0) return false;
            if (bodyStoragePrefs[folderId] === false) {
                const emailsToSave = emails.map(email => ({ ...email, body: undefined, bodyRemoved: true }));
                await saveEmailsToFolder(folderId, emailsToSave, deleteAfterArchive);
            } else {
                await saveEmailsToFolder(folderId, emails, deleteAfterArchive);
            }
            setArchiveStatusVersion((v) => v + 1);
            setOpenedProject(folderId);
            setCurrentView("project");
            return true;
        } catch (error) {
            console.error("Quick save failed:", error);
            return false;
        }
    }, [bodyStoragePrefs]);

    const refreshFavorites = useCallback(async (): Promise<FolderItem[]> => {
        try {
            const favs = await getUserFavorites();
            setFavorites(favs);
            return favs;
        } catch (error) {
            console.error("Error refreshing favorites:", error);
            return [];
        }
    }, []);

    // Initial favorites load
    useEffect(() => {
        if (!getSetting("showArchiveNotifications", true)) {
            setShowToast(false);
        }

        const loadFavorites = async () => {
            try {
                const favs = await getUserFavorites();
                setFavorites(favs);
            } catch (error) {
                console.error("Error loading favorites on app init:", error);
            }
        };
        loadFavorites();
    }, []);

    // Sync keywords map when favorites change
    useEffect(() => {
        const keywordsMap = new Map<string, string[]>();
        favorites.forEach((folder) => {
            if (folder.hidden) return;
            const keywords: string[] = [];
            if (folder.projectNumber) keywords.push(folder.projectNumber);
            if (folder.keywords && folder.keywords.length > 0) keywords.push(...folder.keywords);
            if (keywords.length > 0) keywordsMap.set(folder.id!, keywords);
        });
        setKeywordsMap(new Map(keywordsMap));
        setFolderMap(new Map(favorites.map((f) => [f.id!, f.name])));
    }, [favorites]);

    // Periodic archive status polling
    useEffect(() => {
        if (currentView !== "history" && currentView !== "project") {
            return; // Only poll when in history or project view
        }

        const POLL_INTERVAL = 30_000; // 30 seconds

        const checkArchiveStatuses = async () => {
            try {
                const history: ArchiveHistoryV2[] = await getArchiveHistory();
                const prevStatuses = previousStatusesRef.current;
                const nextStatuses = new Map<string, string>();

                for (const email of history) {
                    const key = email.messageId;
                    const status = email.archiveStatus;
                    nextStatuses.set(key, status);
                }

                // If any status changed, bump the version to trigger view refreshes
                if (prevStatuses.size > 0) {
                    for (const [key, status] of nextStatuses) {
                        if (prevStatuses.get(key) !== status) {
                            setArchiveStatusVersion((v) => v + 1);
                            break;
                        }
                    }
                }

                previousStatusesRef.current = nextStatuses;
            } catch (error) {
                console.error("Archive status poll failed:", error);
            }
        };

        const seedStatuses = async () => {
            try {
                const history: ArchiveHistoryV2[] = await getArchiveHistory();
                const statuses = new Map<string, string>();
                for (const email of history) {
                    statuses.set(email.messageId, email.archiveStatus);
                }
                previousStatusesRef.current = statuses;
            } catch (error) {
                console.error("Failed to seed archive statuses:", error);
            }
        };

        seedStatuses();
        const intervalId = setInterval(checkArchiveStatuses, POLL_INTERVAL);
        return () => clearInterval(intervalId);
    }, [currentView]);

    // Handle initialized-with-suggestion event
    useEffect(() => {
        const handler = (event: CustomEvent) => {
            const context = event.detail as { suggestedFolders?: string[] };
            const suggestedFolders = context.suggestedFolders ?? [];
            const suggestedFolder = suggestedFolders[0];
            if (suggestedFolder) {
                setSelectedFolders(suggestedFolders);
                navigateToProject(suggestedFolder);
                navigateToSave();
            }
        };
        window.addEventListener("initialized-with-suggestion", handler as EventListener);
        return () => {
            window.removeEventListener("initialized-with-suggestion", handler as EventListener);
        };
    }, [navigateToProject, navigateToSave]);

    // Persist state
    useEffect(() => {
        saveAppState({ currentView, openedProject, bodyStoragePrefs });
    }, [currentView, openedProject, bodyStoragePrefs]);


    // Open email preview
    const openEmailPreviewDialog = useCallback(async (email: EmailData, setSelectedEmail?: React.Dispatch<React.SetStateAction<EmailData | null>>, handleRemoveEmailBody?: (emailId: string) => Promise<void>) => {
        let accessToken: string | undefined;
        try {
            accessToken = await getAccessTokenForDialog();
        } catch (tokenError) {
            console.warn("Could not prefetch access token for preview dialog:", tokenError);
        }

        const emailData = {
            id: email.id,
            subject: email.subject,
            sender: email.sender,
            recipient: email.recipient,
            date: email.date.toLocaleDateString() + " " + email.date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
            archiveStatus: email.archiveStatus,
            hasAttachments: email.hasAttachments,
            allowBodyRemoval: email.allowBodyRemoval,
            bodyRemoved: email.bodyRemoved,
            accessToken,
            themeMode: getSetting<string>("themeMode", "system"),
        };

        const encodedData = encodeURIComponent(JSON.stringify(emailData));
        const dialogUrl = `${window.location.origin}/emailPreview.html#${encodedData}`;
        Office.context.ui.displayDialogAsync(
            dialogUrl,
            { height: 60, width: 50, displayInIframe: true },
            (result) => {
                if (result.status === Office.AsyncResultStatus.Failed) {
                    console.error("Failed to open dialog:", result.error.message);
                    return;
                }

                dialogRef.current = result.value;

                dialogRef.current.addEventHandler(
                    Office.EventType.DialogMessageReceived,
                    (arg: { message: string; origin: string | undefined } | { error: number }) => {
                        if ("message" in arg) {
                            try {
                                const message = JSON.parse(arg.message);
                                if (message.action === "close") {
                                    dialogRef.current?.close();
                                    dialogRef.current = null;
                                    setSelectedEmail?.(null);
                                } else if (message.action === "removeBody" && message.emailId) {
                                    handleRemoveEmailBody?.(message.emailId);
                                }
                            } catch (e) {
                                console.error("Error parsing dialog message:", e);
                            }
                        }
                    }
                );

                dialogRef.current.addEventHandler(Office.EventType.DialogEventReceived, () => {
                    dialogRef.current = null;
                    setSelectedEmail?.(null);
                });
            }
        );
    }, []);

    const value: AppContextType = {
        currentView,
        openedProject,
        navigateToFolders,
        navigateToProject,
        navigateToAddFavorites,
        navigateToSave,
        navigateToHistory,
        navigateToSettings,
        favorites,
        setFavorites,
        bodyStoragePrefs,
        handleBodyStoragePreference,
        searchTerm,
        setSearchTerm,
        selectedFolder,
        setSelectedFolder,
        selectedFolders,
        setSelectedFolders,
        handleSaveEmails,
        handleAddToFavorites,
        refreshFavorites,
        archiveStatusVersion,
        openEmailPreviewDialog,
        quickSaveEmails,
    };

    return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
